import Header from "../../header/Header";
import FadeIn from "../../../general/FadeIn";
import OrangeButton from "../../../general/OrangeButton";
import React, { useState } from "react";
import MessageModal from "../../../general/MessageModal";
import CorrecaoModal from "../../../general/CorrecaoModal";
import Api from "../../../../services/Api";
import { useResponsive } from "../../../../hooks/useResponsive";

// Formata data de YYYY-MM-DD para DD/MM/YYYY
const formatDate = (d) => {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
};

/**
 * MemorialPage — página de homenagem ao sepultado.
 * Exibe foto (ou avatar placeholder), nome, datas e mensagem da família.
 * @param {function} navigate  - redireciona para outra página
 * @param {object}   user      - dados do usuário logado (repassados ao Header)
 * @param {function} onLogout  - desloga o usuário
 * @param {object}   falecido  - dados do sepultado selecionado na SearchPage
 */
function MemorialPage({
  navigate,
  user,
  onLogout,
  falecido,
  onFalecidoUpdated,
}) {
  const { isMobile } = useResponsive();
  const [showModal, setShowModal] = useState(false);
  const [correcaoModalOpen, setCorrecaoModalOpen] = useState(false);

  const photoSize = isMobile ? 160 : 240;

  return (
    <div style={{ minHeight: "100vh", background: "#fafafa" }}>
      <Header
        onLogin={() => navigate("login")}
        onHome={() => navigate("search")}
        user={user}
        onLogout={onLogout}
        navigate={navigate}
        onFalecidoUpdated={onFalecidoUpdated}
      />

      <div
        style={{
          maxWidth: 700,
          margin: "0 auto",
          padding: isMobile ? "32px 16px" : "60px 24px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            maxWidth: 900,
            margin: "0 auto",
            padding: isMobile ? "32px 16px" : "60px 24px",
            textAlign: "center",
          }}
        >
          <FadeIn>
            <div
              style={{
                width: photoSize,
                height: photoSize,
                borderRadius: "50%",
                background: "linear-gradient(180deg, #faf0e6 0%, #f5dcc8 100%)",
                margin: "0 auto 32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
            >
              {falecido?.foto_rosto_path ? (
                <img
                  src={Api.getFalecidoFotoUrl(falecido.foto_rosto_path)}
                  alt={falecido.nome_completo}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <svg width="140" height="140" viewBox="0 0 140 140" fill="none">
                  <circle cx="70" cy="55" r="30" fill="#e8b89a" />
                  <ellipse cx="70" cy="48" rx="24" ry="18" fill="#ccc" />
                  <circle cx="60" cy="58" r="4" fill="#fff" />
                  <circle cx="60" cy="58" r="2" fill="#333" />
                  <circle cx="80" cy="58" r="4" fill="#fff" />
                  <circle cx="80" cy="58" r="2" fill="#333" />
                  <ellipse cx="70" cy="72" rx="8" ry="3" fill="#d4a088" />
                  <rect
                    x="48"
                    y="85"
                    width="44"
                    height="50"
                    rx="6"
                    fill="#b08060"
                  />
                  <rect
                    x="52"
                    y="85"
                    width="36"
                    height="45"
                    rx="4"
                    fill="#f5dcc8"
                  />
                </svg>
              )}
            </div>
          </FadeIn>

          <FadeIn delay={200}>
            <h1
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: isMobile ? 26 : 42,
                fontWeight: 700,
                color: "#2d2d2d",
                marginBottom: 24,
              }}
            >
              {falecido?.nome_completo || "—"}
            </h1>
          </FadeIn>

          <FadeIn delay={350}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: isMobile ? 20 : 60,
                marginBottom: 32,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: isMobile ? 20 : 28 }}>★</span>
                <span
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: isMobile ? 16 : 22,
                    fontWeight: 600,
                  }}
                >
                  {formatDate(falecido?.data_nascimento)}
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: isMobile ? 20 : 28 }}>✝</span>
                <span
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: isMobile ? 16 : 22,
                    fontWeight: 600,
                  }}
                >
                  {formatDate(falecido?.data_falecimento)}
                </span>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={500}>
            <div
              style={{
                maxWidth: 700,
                margin: "0 auto 50px",
                padding: "24px",
                background: "#fff",
                borderRadius: 16,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              }}
            >
              <p
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: isMobile ? 18 : 22,
                  fontStyle: "italic",
                  color: "#E8956A",
                  lineHeight: 1.8,
                  margin: 0,
                }}
              >
                {falecido?.mensagem_familia ||
                  "Com amor e muita saudade, de todos os seus amigos que nunca se esquecerão de você."}
              </p>
            </div>
          </FadeIn>

          {falecido?.video && (
            <FadeIn delay={600}>
              <div
                style={{
                  marginBottom: 50,
                }}
              >
                <h2
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: isMobile ? 24 : 32,
                    color: "#2d2d2d",
                    marginBottom: 20,
                  }}
                >
                  Homenagem em Vídeo
                </h2>

                <video
                  controls
                  preload="metadata"
                  style={{
                    width: "100%",
                    maxWidth: 850,
                    borderRadius: 16,
                    boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                  }}
                >
                  <source
                    src={Api.getFalecidoVideoUrl(falecido.video)}
                    type="video/mp4"
                  />
                </video>
              </div>
            </FadeIn>
          )}

          {falecido?.historia && (
            <FadeIn delay={700}>
              <div
                style={{
                  textAlign: "left",
                  maxWidth: 850,
                  margin: "0 auto 50px",
                }}
              >
                <h2
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: isMobile ? 24 : 32,
                    color: "#2d2d2d",
                    textAlign: "center",
                    marginBottom: 24,
                  }}
                >
                  História
                </h2>

                <div
                  style={{
                    background: "#fff",
                    borderRadius: 16,
                    padding: isMobile ? 20 : 32,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: isMobile ? 15 : 17,
                      lineHeight: 1.9,
                      color: "#555",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {falecido.historia}
                  </div>
                </div>
              </div>
            </FadeIn>
          )}

          <FadeIn delay={800}>
            <div
              style={{
                marginTop: 30,
                display: "flex",
                justifyContent: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <OrangeButton
                onClick={() => setShowModal(true)}
                style={{ maxWidth: 240 }}
              >
                Prestar Homenagem
              </OrangeButton>

              <button
                onClick={() => setCorrecaoModalOpen(true)}
                style={{
                  padding: "14px 28px",
                  borderRadius: 40,
                  border: "2px solid #E8956A",
                  background: "#fff",
                  color: "#E8956A",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Solicitar correção
              </button>
            </div>
          </FadeIn>
        </div>
      </div>
      <CorrecaoModal
        isOpen={correcaoModalOpen}
        onClose={() => setCorrecaoModalOpen(false)}
        falecido={falecido}
      />
      <MessageModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={(data) => {
          setShowModal(false);
        }}
        falecidoId={falecido?.id}
      />
    </div>
  );
}

export default MemorialPage;
